import { useContext } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

import { useActiveEditContext } from "../../../hooks/use_active_edit_context";
import { useForceUserEditConclude } from "../../modals/force_user_edit_conclude/index";
import { VersionedDataContext } from "../../../contexts/versioned_data/context";

export const EditModeToggle = () => {
  const { editMode, setEditMode, discardChanges, loading } =
    useActiveEditContext();
  const { openForceUserEditModal } = useForceUserEditConclude();
  const { pathname } = useLocation();
  const {
    mainRun,
    lastSuccessfulRun,
    engineInProgress,
    justTriggeredRun,
    loading: runsLoading,
  } = useContext(VersionedDataContext);

  const handleEdit = () => setEditMode(true);
  const handleSave = () => openForceUserEditModal(1);

  // Disable edit for old runs on purchasing calendar only
  const isPurchasingCalendar = pathname.startsWith("/purchasing-calendar");
  const viewedRun = mainRun ?? lastSuccessfulRun;
  const isOldRun =
    isPurchasingCalendar &&
    viewedRun != null &&
    lastSuccessfulRun != null &&
    viewedRun.id !== lastSuccessfulRun.id;
  const isRunning = runsLoading || engineInProgress || justTriggeredRun !== null;
  const editDisabled = loading || isOldRun || isRunning;
  const editTooltip = isRunning
    ? "Cannot edit while a run is in progress"
    : isOldRun
      ? "Editing is only available on the most recent run"
      : "Edit Forecast";

  if (!editMode) {
    return (
      <Tooltip title={editTooltip} arrow>
        <Box sx={{ display: "flex", alignItems: "center", p: "5px" }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleEdit}
            disabled={editDisabled}
            sx={{
              fontSize: "0.75rem",
              minHeight: "32px",
              minWidth: "32px",
              borderRadius: "50%",
              p: "5px",
            }}
          >
            <EditIcon fontSize="small" />
          </Button>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "5px" }}>
      <Tooltip title="Discard Changes" arrow>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={discardChanges}
          disabled={loading}
          sx={{
            fontSize: "0.75rem",
            minHeight: "32px",
            minWidth: "32px",
            borderRadius: "50%",
            p: "5px",
          }}
        >
          <DeleteIcon fontSize="small" />
        </Button>
      </Tooltip>
      <Tooltip title="Save Changes" arrow>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={loading}
          sx={{
            fontSize: "0.75rem",
            minHeight: "32px",
            minWidth: "32px",
            borderRadius: "50%",
            p: "5px",
          }}
        >
          <SaveIcon fontSize="small" />
        </Button>
      </Tooltip>
    </Box>
  );
};
